import { type ColumnDef, getCoreRowModel } from '@tanstack/react-table'
import { type ReactNode, useId, useMemo } from 'react'
import { useDataTable } from '../../hooks/use-data-table'
import { cn } from '../../lib/utils'
import { Badge } from '../ui/badge'
import { DataTable } from '../ui/data-table'
import type { ImportReportError } from './import-report'

export type ImportProblemsLabels = {
  sheet: string
  row: string
  column: string
  wholeRow: string
}

/**
 * One list of an import report's row findings: the problems that stop the
 * import, or the warnings it imports with. The tone carries the difference;
 * the heading and the count say it in words too.
 */
export function ImportProblems({
  rows,
  tone,
  heading,
  messageHeader,
  labels,
  note,
  count,
  'data-testid': testId,
}: {
  rows: ImportReportError[]
  tone: 'danger' | 'warning'
  heading: string
  /** The header of the message column: "Problem" or "Warning". */
  messageHeader: string
  labels: ImportProblemsLabels
  /** A line under the heading, such as "Showing the first 500 of 900 problems". */
  note?: ReactNode
  /** A count beside the heading, when the list's length is the figure to show. */
  count?: number
  'data-testid': string
}) {
  const id = useId()
  const columns = useMemo<ColumnDef<ImportReportError>[]>(
    () => [
      {
        id: 'sheet',
        header: labels.sheet,
        cell: ({ row }) => <span className="whitespace-normal">{row.original.section_label}</span>,
        meta: { phone: 'meta', phoneLabel: labels.sheet },
      },
      {
        id: 'row',
        header: labels.row,
        cell: ({ row }) => row.original.row,
        meta: { numeric: true, phone: 'meta', phoneLabel: labels.row },
      },
      {
        id: 'column',
        header: labels.column,
        cell: ({ row }) => (
          <span className="whitespace-normal">{row.original.column_label ?? labels.wholeRow}</span>
        ),
        meta: { phone: 'meta', phoneLabel: labels.column },
      },
      {
        id: 'problem',
        header: messageHeader,
        cell: ({ row }) => <span className="whitespace-normal">{row.original.message}</span>,
        meta: { phone: 'primary' },
      },
    ],
    [labels, messageHeader],
  )
  const table = useDataTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_row, index) => String(index),
  })

  return (
    <section
      aria-labelledby={id}
      className={cn(tone === 'warning' && 'border-s-2 border-warning ps-3')}
      data-tone={tone}
    >
      <div className="mb-2 flex items-center gap-2">
        <h3
          id={id}
          className={cn(
            'text-label',
            tone === 'warning' ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {heading}
        </h3>
        {count === undefined ? null : (
          <Badge tone={tone} fill="solid" className="tabular-nums" data-testid={`${testId}-count`}>
            {count}
          </Badge>
        )}
      </div>
      {note ? <p className="mb-2 text-small text-muted-foreground">{note}</p> : null}
      <DataTable table={table} phoneLayout="rows" caption={heading} data-testid={testId} />
    </section>
  )
}
